import { ContactDataChangeTypeEnum } from '@elearning-platform/api-types/enums/api/profile';
import { UpdateAgreementInterface } from '@elearning-platform/api-types/interfaces';
import {
  Breakpoint,
  ButtonSize,
  ButtonTheme,
  ButtonType,
  CheckboxSize,
  CheckboxType,
  CheckboxVariant,
  FullScreenAlertVariant,
  TypographyVariant,
  UseNotifications,
  useNotifications,
  UseState,
} from '@elearning-platform/ui';
import { FormikProvider, FormikValues, useFormik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { resendContactDataConfirmationToken, updateContactData } from '../../../../api/requests';

import { DynamicFormField } from './components/dynamicFormField.component';
import { ReactComponent as ShapeDesktop } from './images/shapeDesktop.svg';
import { ReactComponent as ShapeMobile } from './images/shapeMobile.svg';
import { useMissingProfileData } from './missingProfileData.hook';
import { createValidationSchema } from './missingProfileData.schema';
import {
  AgreementsContainer,
  Container,
  Disclaimer,
  Divider,
  FormContainer,
  FormFieldsContainer,
  ImageContainer,
  InfoBoxContent,
  StyledButton,
  StyledCheckboxField,
  StyledSVG,
  Title,
} from './missingProfileData.styled';
import {
  MissingProfileDataFields,
  MissingProfileDataProps,
  MissingProfileFormData,
  UseMissingProfileData,
} from './missingProfileData.types';

export const MissingProfileData: React.FC<MissingProfileDataProps> = (props: MissingProfileDataProps): JSX.Element => {
  const { formType, data }: MissingProfileDataProps = props;
  const { t }: TransProps<never> = useTranslation();
  const {
    showFullscreenAlert,
    hideFullscreenAlert,
    showPopup,
    hidePopup,
    setFullscreenAlertAcceptButtonDisabledLockTime,
  }: UseNotifications = useNotifications();
  const [isRequestInProgress, setIsRequestInProgress]: UseState<boolean> = useState<boolean>(false);
  const isTablet: boolean = useMediaQuery({
    query: Breakpoint.Tablet,
  });
  const { getConfig }: UseMissingProfileData = useMissingProfileData();

  const config: MissingProfileDataFields | null = useMemo(
    (): MissingProfileDataFields | null => getConfig(formType, data),
    [formType, data, getConfig]
  );

  const formFields: MissingProfileFormData[] = useMemo(
    (): MissingProfileFormData[] => config?.form.filter((item: MissingProfileFormData): boolean => item.fieldType !== 'boolean') ?? [],
    [config?.form]
  );

  const agreementFields: MissingProfileFormData[] = useMemo(
    (): MissingProfileFormData[] => config?.form.filter((item: MissingProfileFormData): boolean => item.fieldType === 'boolean') ?? [],
    [config?.form]
  );

  useEffect(
    (): void => {
      if (data.value && config) {
        showFullscreenAlert({
          title: t(config.successMessage, { value: data.value }),
          description: t(config.successDescription),
          acceptButtonData: {
            label: t('competence.app.actionRequiredToAccess.missingProfileData.popup.sendAgain'),
            onClick: (): void => {
              resendContactDataConfirmationToken({ value: data.value, type: ContactDataChangeTypeEnum.PrivateEmail })
                .then((): void => setFullscreenAlertAcceptButtonDisabledLockTime(30_000))
                .catch((error: Error): void => {
                  showPopup({
                    title: t('competence.app.global.errorOccurred'),
                    description: error.message,
                    acceptButtonData: { label: t('competence.app.global.close'), onClick: (): void => hidePopup() },
                  });
                });
            },
          },
          cancelButtonData: {
            label: t('competence.app.actionRequiredToAccess.missingProfileData.popup.editData'),
            onClick: (): void => {
              hideFullscreenAlert();
            },
          },
          variant: FullScreenAlertVariant.Dark,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.value, config, t, hideFullscreenAlert]
  );

  const onSubmit: (values: FormikValues) => void = (values: FormikValues): void => {
    if (!config) {
      return;
    }

    const agreementsValue: UpdateAgreementInterface[] = Object.keys(values)
      .filter((valueKey: string): boolean => valueKey !== 'value')
      .map((valueKey: string): UpdateAgreementInterface => ({ name: valueKey, agree: values[valueKey] }));
    setIsRequestInProgress(true);
    updateContactData({
      value: values['value'],
      type: ContactDataChangeTypeEnum.PrivateEmail,
      agreements: agreementsValue,
    }).then((): void => {
      showFullscreenAlert({
        title: t(config.successMessage, { value: values['value'] }),
        description: t(config.successDescription),
        acceptButtonData: {
          label: t('competence.app.actionRequiredToAccess.missingProfileData.popup.sendAgain'),
          onClick: (): void => {
            resendContactDataConfirmationToken({ value: values['value'], type: ContactDataChangeTypeEnum.PrivateEmail })
              .catch((error: Error): void => {
                showPopup({
                  title: t('competence.app.global.errorOccurred'),
                  description: error.message,
                  acceptButtonData: { label: t('competence.app.global.close'), onClick: (): void => hidePopup() },
                });
              });
          },
        },
        cancelButtonData: {
          label: t('competence.app.actionRequiredToAccess.missingProfileData.popup.editData'),
          onClick: (): void => {
            hideFullscreenAlert();
          },
        },
        variant: FullScreenAlertVariant.Dark,
      });
    })
      .catch((error: Error): void => {
        showPopup({
          title: t('competence.app.global.errorOccurred'),
          description: error.message,
          acceptButtonData: { label: t('competence.app.global.close'), onClick: (): void => hidePopup() },
        });
      })
      .finally((): void => setIsRequestInProgress(false));
  };

  const formik: ReturnType<typeof useFormik> = useFormik<FormikValues>({
    initialValues: data.value
      ? { value: data.value }
      : {},
    isInitialValid: false,
    validateOnChange: true,
    validationSchema: createValidationSchema(config?.form ?? [], t),
    onSubmit,
    enableReinitialize: true,
  });

  const hasSubmitted: boolean = formik.submitCount > 0;

  return (
    <FormikProvider value={formik}>
      <Container>
        <ImageContainer>
          <StyledSVG as={isTablet ? ShapeDesktop : ShapeMobile} />
        </ImageContainer>
        <FormContainer onSubmit={formik.handleSubmit}>
          <Title variant={TypographyVariant.TextLG}>{t(config?.title)}</Title>
          <FormFieldsContainer>
            {formFields.map((field: MissingProfileFormData): JSX.Element => (
              <DynamicFormField field={field} hasSubmitted={hasSubmitted} key={field.name} />
            ))}
          </FormFieldsContainer>
          {!!(agreementFields.length > 0 && config?.infoBox) && (
            <AgreementsContainer>
              <InfoBoxContent text={t(config.infoBox)} />
              <Disclaimer variant={TypographyVariant.TextXXS}>
                {t('competence.app.actionRequiredToAccess.missingProfileData.requiredData')}
              </Disclaimer>
              <Divider />
              {agreementFields.map((item: MissingProfileFormData): JSX.Element => (
                <StyledCheckboxField
                  checked={formik.values[item.name] || false}
                  errorMessage={t('competence.app.actionRequiredToAccess.missingProfileData.validation.fieldRequired')}
                  key={item.name}
                  label={`<div>${item.required ? "<span class='astrix'>*</span>" : ''}${item.label}</div>`}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                    void formik.setFieldValue(item.name, e.currentTarget.checked);
                  }}
                  size={CheckboxSize.Small}
                  type={CheckboxType.Checkbox}
                  variant={CheckboxVariant.SimpleText}
                  withError={!!formik.errors[item.name] && hasSubmitted}
                />
              ))}
            </AgreementsContainer>
          )}
          <StyledButton
            disabled={!formik.isValid && hasSubmitted}
            isLoading={isRequestInProgress}
            label={t('competence.app.actionRequiredToAccess.missingProfileData.send')}
            size={ButtonSize.Large}
            type={ButtonType.Submit}
            variant={ButtonTheme.PrimaryBrand}
          />
        </FormContainer>
      </Container>
    </FormikProvider>
  );
};
